import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  FluentProvider,
  IdPrefixProvider,
  type Theme,
} from '@fluentui/react-components';
import {
  fetchUserPreference,
  getApiErrorMessage,
  parseColorScheme,
  resolveColorScheme,
  updateColorScheme,
  type ColorSchemePreference,
} from '@moko/utils';
import { getAccessToken } from '@moko/utils/src/authHelper';
import useFetch from '../hooks/useFetch';
import useMutation from '../hooks/useMutation';
import { ToastHost } from '../Toast';
import UniversalLoading from '../UniversalLoading/UniversalLoading';
import { ColorSchemeContext } from './ColorSchemeContext';
import { MokoThemeContext } from './ThemeContext';
import {
  applyThemeCss,
  buildMokoTheme,
  type MokoThemeConfig,
} from './themeConfig';

type MokoThemeProviderProps = {
  children: ReactNode;
  idPrefix?: string;
} & (
  | {
      config: MokoThemeConfig;
      theme?: never;
      colorScheme?: ColorSchemePreference;
      onColorSchemeChange?: (colorScheme: ColorSchemePreference) => void;
    }
  | {
      theme: Theme;
      config?: never;
      colorScheme?: never;
      onColorSchemeChange?: never;
    }
);

const ThemeTree = ({
  children,
  theme,
  idPrefix,
}: {
  children: ReactNode;
  theme: Theme;
  idPrefix?: string;
}) => {
  const tree = (
    <MokoThemeContext.Provider value={theme}>
      <FluentProvider theme={theme}>
        <ToastHost />
        {children}
      </FluentProvider>
    </MokoThemeContext.Provider>
  );

  return idPrefix ? (
    <IdPrefixProvider value={idPrefix}>{tree}</IdPrefixProvider>
  ) : (
    tree
  );
};

const ConfigurableTheme = ({
  children,
  config,
  idPrefix,
  colorScheme: colorSchemeProp,
  onColorSchemeChange,
}: {
  children: ReactNode;
  config: MokoThemeConfig;
  idPrefix?: string;
  colorScheme?: ColorSchemePreference;
  onColorSchemeChange?: (colorScheme: ColorSchemePreference) => void;
}) => {
  const [chosen, setChosen] = useState<ColorSchemePreference | null>(null);
  const [osGeneration, setOsGeneration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ownsFetch = colorSchemeProp == null;
  const preference = useFetch({
    queryKey: ['color-scheme'],
    enabled: ownsFetch && Boolean(getAccessToken()),
    queryFn: fetchUserPreference,
  });
  const saveScheme = useMutation(
    (next: ColorSchemePreference, abortController) =>
      updateColorScheme(abortController, next)
  );
  const colorScheme =
    chosen ??
    colorSchemeProp ??
    (preference.data ? parseColorScheme(preference.data.preference) : 'light');
  const paint = resolveColorScheme(colorScheme);
  const theme = useMemo(
    () => buildMokoTheme(config, paint),
    [config, paint, osGeneration]
  );

  useEffect(() => {
    if (
      colorScheme !== 'system' ||
      typeof window === 'undefined' ||
      !window.matchMedia
    ) {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setOsGeneration((value) => value + 1);
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [colorScheme]);

  useLayoutEffect(() => {
    applyThemeCss(config, theme, paint);
  }, [config, theme, paint]);

  const changeColorScheme = (next: ColorSchemePreference) => {
    if (saveScheme.loading || next === colorScheme) return;
    const previous = colorScheme;
    setChosen(next);
    setError(null);
    onColorSchemeChange?.(next);
    void saveScheme.mutate(next).then((outcome) => {
      if (outcome.status !== 'error') return;
      setChosen(previous);
      onColorSchemeChange?.(previous);
      setError(
        getApiErrorMessage(outcome.error, 'Failed to save the color scheme.')
      );
    });
  };

  if (ownsFetch && preference.loading) {
    return (
      <ThemeTree theme={theme} idPrefix={idPrefix}>
        <UniversalLoading />
      </ThemeTree>
    );
  }

  return (
    <ColorSchemeContext.Provider
      value={{
        colorScheme,
        saving: saveScheme.loading,
        error,
        setColorScheme: changeColorScheme,
      }}
    >
      <ThemeTree theme={theme} idPrefix={idPrefix}>
        {children}
      </ThemeTree>
    </ColorSchemeContext.Provider>
  );
};

export const MokoThemeProvider = ({
  children,
  config,
  theme: themeProp,
  idPrefix,
  colorScheme,
  onColorSchemeChange,
}: MokoThemeProviderProps) => {
  if (themeProp) {
    return (
      <ThemeTree theme={themeProp} idPrefix={idPrefix}>
        {children}
      </ThemeTree>
    );
  }

  return (
    <ConfigurableTheme
      config={config}
      idPrefix={idPrefix}
      colorScheme={colorScheme}
      onColorSchemeChange={onColorSchemeChange}
    >
      {children}
    </ConfigurableTheme>
  );
};
